import React, { useState, useEffect } from 'react';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { Breadcrumb } from '@/components/Breadcrumb';
import { GradeBadge } from '@/components/GradeBadge';
import type { AdminUsersResponse, AdminConversationResponse } from '@/types/admin';
import type { User } from '@/types/auth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './AdminUsers.css';
import '../styles/common.css';

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUsersResponse[]>([]);
  const [conversations, setConversations] = useState<{ [key: string]: AdminConversationResponse[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const moduleId = searchParams.get('moduleId');

  useEffect(() => {
    fetchUserInfo();
    if (moduleId) {
      fetchUsers();
    } else {
      setError('No module ID provided');
      setLoading(false);
    }
  }, [moduleId]);

  const fetchUserInfo = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const fetchUsers = async () => {
    if (!moduleId) return;
    
    try {
      setLoading(true);
      setError(null);
      const usersData = await adminService.getUsersByModule(moduleId);
      setUsers(usersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async (userId: string) => {
    try {
      const conversationsData = await adminService.getConversationsByUser(userId);
      // Sort conversations by date descending
      const sortedConversations = conversationsData.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setConversations(prev => ({
        ...prev,
        [userId]: sortedConversations
      }));
    } catch (err) {
      // Fehler wird stillschweigend ignoriert
    }
  };


  const handleViewConversation = (conversationId: string, studentUser: AdminUsersResponse) => {
    sessionStorage.setItem('adminModuleId', moduleId || '');
    sessionStorage.setItem('adminUserId', studentUser.id);
    sessionStorage.setItem('adminUserFirstName', studentUser.firstName);
    sessionStorage.setItem('adminUserLastName', studentUser.lastName);
    sessionStorage.removeItem('adminBackTo');
    sessionStorage.removeItem('adminBackDate');
    navigate(`/admin/conversation?conversationId=${conversationId}`);
  };

  const toggleExpanded = async (userId: string) => {
    setExpandedUser(expandedUser === userId ? null : userId);
    const isExpanding = expandedUser !== userId;
    if (isExpanding && !conversations[userId]) {
      await fetchConversations(userId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };


  return (
    <div className="admin-users-container">
      <AppHeader 
        user={user} 
        title="KI Expert - Admin" 
        subtitle={`Users for Module: ${moduleId}`} 
      />
      
      <main className="list-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={[
              { label: 'Modules', path: '/admin/modules' },
              { label: 'Users' },
            ]} />
            <h2 className="navigation-bar-title">Users</h2>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error loading</h3>
            <p>{error}</p>
            <button onClick={fetchUsers} className="btn btn-primary">
              Try again
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-container">
            <div className="empty-icon">👥</div>
            <h3>No users found</h3>
            <p>No users are assigned to this module.</p>
          </div>
        ) : (
          <div className="list list--compact">
            <ul>
            {users.map(user => (
              <li key={user.id} className="list-item">

                <div className="list-item-header" onClick={() => toggleExpanded(user.id)}>
                  <div className="list-item-header-info">
                    <h3 className="list-item-header-title">{user.firstName} {user.lastName}</h3>
                     <span className="admin-user-id">ID: {user.id}</span>
                  </div>
                </div>
                
                {expandedUser === user.id && (
                  <div className="list-item-body">
                    <div className="admin-user-meta">
                      <h4>User Information:</h4>
                      <p><strong>Username:</strong> {user.username}</p>
                      <p><strong>Name:</strong> {user.firstName} {user.lastName}</p>
                    </div>
                    
                    <div className="conversations-section">
                      <h4>Conversations:</h4>
                      {expandedUser && conversations[expandedUser] && conversations[expandedUser].length > 0 ? (
                        <div className="admin-conversations-list">
                          {conversations[expandedUser].map(conversation => (
                            <div
                              key={conversation.id}
                              className={`admin-conversation-item${conversation.isDiscussed ? ' discussed' : ''}${conversation.isAdjusted ? ' adjusted' : ''}`}
                              onClick={() => handleViewConversation(conversation.id, user)}
                            >
                              <div className="admin-conversation-header">
                                <span className="admin-subject-number">{conversation.subjectNumber}</span>
                                <span className="admin-subject-title">{conversation.subjectTitle}</span>
                              </div>
                              <div className="admin-conversation-details">
                                <span className="admin-conversation-date">{formatDate(conversation.createdAt)}</span>
                                <GradeBadge grade={conversation.grade} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-conversations">No conversations found for this user.</p>
                      )}
                    </div>

                    <div className="actions-list">
                      <button className="btn btn-primary">View Details</button>
                      <button className="btn btn-secondary">Edit User</button>
                      <button className="btn btn-danger">Remove from Module</button>
                    </div>
                  </div>
                )}
              </li>
            ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminUsers;
